export const costCategories = [
  "materials",
  "labor",
  "equipment",
  "subcontractor",
  "permits",
  "insurance",
  "other",
] as const;
export type CostCategory = (typeof costCategories)[number];
export type CategoryAmounts = Record<CostCategory, string>;
export interface CostReconciliation {
  duplicate: string;
  canonical: string;
  reason: string;
}
export interface CostAllocation {
  order: string;
  source: string;
  amount: string;
}
export interface JobCostPlan {
  budget: CategoryAmounts;
  remaining: CategoryAmounts;
  remaining_modes?: Record<CostCategory, "budget" | "manual">;
  margin_floor: string;
  overhead: string;
  note: string;
  reconciliations: CostReconciliation[];
  allocations: CostAllocation[];
  complete_categories: CostCategory[];
}
export interface Plan<T> {
  revision: number;
  data: Partial<T>;
  history: { revision: number; data: T; at: string; actor: string }[];
}
export interface CostSource {
  ref: string;
  label: string;
  category: string;
  amount: string;
  date: string;
  url: string;
  excluded: boolean;
  order_ref?: string;
}
export interface CostOrder {
  ref: string;
  label: string;
  amount: string;
  remaining: string;
  date: string | null;
  status: string;
  url: string;
}
export interface JobProfit {
  job_id: number;
  name: string;
  job_number: string;
  url: string;
  board: string;
  contract_value: string;
  actual_costs: string;
  committed_costs: string;
  additional_remaining: string;
  projected_costs: string;
  forecast_by_category?: Record<CostCategory, {
    mode: "budget" | "manual";
    budget: string;
    budget_used: string;
    committed: string;
    remaining: string;
  }>;
  approved_budget: string | null;
  budget_variance: string | null;
  gross_profit: string;
  projected_gross_profit: string;
  projected_margin: string | null;
  margin_floor: string;
  below_floor: boolean;
  overhead_allowance: string;
  recorded_commissions: string;
  projected_contribution: string;
  collected: string;
  contract_balance: string;
  issued_receivables: string;
  issued_total: string;
  sources: CostSource[];
  orders: CostOrder[];
  warnings: string[];
  costs_by_category: Partial<CategoryAmounts>;
  plan: Plan<JobCostPlan>;
}
export interface ProfitReport {
  jobs: JobProfit[];
  gross_revenue: string;
  total_costs: string;
  net_profit: string;
  profit_margin: string | null;
  warnings: string[];
  note: string;
}
export interface CashExpense {
  label: string;
  amount: string;
  date: string;
  every_weeks: number;
  occurrences: number;
}
export interface CashPlan {
  opening_cash: string;
  as_of: string;
  weeks: number;
  delay_days: number;
  note: string;
  expenses: CashExpense[];
  collections: { invoice_id: number; date: string }[];
  reconciliations: CostReconciliation[];
}
export interface CashEvent {
  ref: string;
  label: string;
  amount: string;
  date: string;
  direction: "in" | "out";
  url: string;
  assumption: string;
  excluded: boolean;
}
export interface CashForecast {
  plan: Plan<CashPlan>;
  opening_cash: string;
  as_of: string;
  delay_days: number;
  warnings: string[];
  events: CashEvent[];
  weeks: {
    week: string;
    receipts: string;
    payments: string;
    closing_cash: string;
    delayed_closing_cash: string;
  }[];
}

// Older approvals used zero as the default, not an explicit completion override.
export function remainingMode(plan: Partial<JobCostPlan>, category: CostCategory) {
  return plan.remaining_modes?.[category] ??
    (Number(plan.remaining?.[category] ?? 0) !== 0 ? "manual" : "budget");
}
